import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, Breadcrumb, Button, Spin, Alert, Typography, Table, Modal, Input, InputNumber, DatePicker, Space, message, Popconfirm, Select, Tag } from 'antd';
import { HomeOutlined, ArrowLeftOutlined, PlusOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import { useTheme } from '../../../context/ThemeContext';
import FormikForm from '../../form/FormikForm';
import axios from 'axios';
import dayjs from 'dayjs';
import { API_BASE_URL as SHARED_API_BASE_URL } from '../../../utils/config';
import { formatMoney } from '../../../utils/currency';
import { trIncludes } from '../../../utils/trText';

const { Title, Text } = Typography;

// 26.10 düzeltmesi (F4.6): sayfa /lokasyon_sozlesme/:maptriksId ile açılıyor. Eskiden
// state'teki form 226 + 'banka_sozlesme_kayit' + uniqueField 'maptriksID' ile açılıyordu:
// şema satırı hata verip yutuluyor, form lokasyondaki rastgele bir YS sözleşmesiyle
// doluyor ve Kaydet yeni bir ys_sozlesme ekliyordu. 'Lokasyon Ekle' lokasyon no'sunu
// sözleşme id'si diye gönderiyordu. Artık:
//   - sözleşme: state.sozlesmeId | ?sozlesme= | (state ys_sozlesme+id ise uniqueValue);
//     yoksa lokasyondaki YS sözleşmelerinden seçilir (tek ise otomatik)
//   - form: seçili sözleşme için 226 + ys_sozlesme + id + Update
//   - liste/ekle/sil: /form/lokasyon-listesi, /form/lokasyon-ekle, /form/lokasyon-sil/{ysk id}
const AYLAR = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const sayiMi = (v) => v !== null && v !== undefined && /^\d+$/.test(String(v)) && String(v) !== '0';

const LokasyonSozlesme = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { maptriksId: urlLokasyon } = useParams();
  const { darkMode } = useTheme();
  
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const [lokasyonlar, setLokasyonlar] = useState([]);
  const [lokasyonModalVisible, setLokasyonModalVisible] = useState(false);
  const [lokasyonListesi, setLokasyonListesi] = useState([]);
  const [lokasyonLoading, setLokasyonLoading] = useState(false);
  const [selectedLokasyon, setSelectedLokasyon] = useState(null);
  const [kiraBedeli, setKiraBedeli] = useState('');
  const [kiraBaslangic, setKiraBaslangic] = useState(null);
  const [lokasyonEklemeLoading, setLokasyonEklemeLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [seciliSozlesme, setSeciliSozlesme] = useState(null);
  const [lokasyondakiSozlesmeler, setLokasyondakiSozlesmeler] = useState([]);
  const [odemeAy, setOdemeAy] = useState(null);
  const [kabinNo, setKabinNo] = useState(null);
  const [sozlesmeBaslik, setSozlesmeBaslik] = useState(null);
  const [baslikDenenen, setBaslikDenenen] = useState(null);

  const API_BASE_URL = SHARED_API_BASE_URL;

  const filteredLokasyonListesi = lokasyonListesi.filter(lokasyon => {
    if (!searchText) return true;
    return (
      trIncludes(lokasyon.maptriksID?.toString(), searchText) ||
      trIncludes(lokasyon['Lokasyon Adı'], searchText) ||
      trIncludes(lokasyon.Durum, searchText) ||
      trIncludes(lokasyon.Kategori, searchText) ||
      trIncludes(lokasyon.İl, searchText) ||
      trIncludes(lokasyon.İlçe, searchText)
    );
  });

  useEffect(() => {
    setLoading(true);
    try {
      const st = location.state || {};
      const searchParams = new URLSearchParams(location.search);
      const stSozlesme = (st.tableName === 'ys_sozlesme' && st.uniqueField === 'id') ? st.uniqueValue : null;
      const adaySozlesme = st.sozlesmeId || searchParams.get('sozlesme') || stSozlesme;
      const sozlesmeId = sayiMi(adaySozlesme) ? String(adaySozlesme) : null;
      setFormData({
        title: st.title || searchParams.get('title') || 'Lokasyon Sözleşmeleri',
        sozlesmeId,
        lokasyonId: sayiMi(urlLokasyon) ? String(urlLokasyon) : null
      });
      setSeciliSozlesme(sozlesmeId);
    } catch (err) {
      console.error('Form verisi alınırken hata:', err);
      setError('Form verisi alınırken bir hata oluştu');
     } finally {
       setLoading(false);
     }
   }, [location, urlLokasyon]);

   // Sözleşme bilinmiyorsa lokasyondaki YS sözleşmeleri seçenek olur (tek ise otomatik seçilir)
   useEffect(() => {
     if (!formData || formData.sozlesmeId || !formData.lokasyonId) return undefined;
     let aktif = true;
     axios.get(`${API_BASE_URL}/form/lokasyon-listesi`, { params: { lokasyon: formData.lokasyonId, tip: 'ys' } })
       .then((res) => {
         if (!aktif) return;
         const gorulen = new Map();
         (res.data?.satirlar || []).forEach((r) => {
           if (r.sozlesme && !gorulen.has(String(r.sozlesme))) {
             gorulen.set(String(r.sozlesme), { value: String(r.sozlesme), label: `#${r.sozlesme} ${r.mUnvan || ''}`.trim() });
           }
         });
         const secenekler = [...gorulen.values()];
         setLokasyondakiSozlesmeler(secenekler);
         if (secenekler.length === 1) setSeciliSozlesme(secenekler[0].value);
       })
       .catch((err) => console.error('Lokasyondaki YS sözleşmeleri yüklenemedi:', err));
     return () => { aktif = false; };
   }, [formData, API_BASE_URL]);

   useEffect(() => {
     if (seciliSozlesme || formData?.lokasyonId) {
       fetchLokasyonlar();
     }
   }, [seciliSozlesme, formData]);

  // 27.10 düzeltmesi: submit-form Update'te 'ekleyen'i düzenleyenle eziyordu (form 226'da
  // ekleyen gizli alan, şemada yok); mevcut değer aynen geri yazılır. Form, seçili
  // sözleşmenin başlığı yüklenmeden açılmaz.
  const baslikHazir = !!(seciliSozlesme && sozlesmeBaslik && String(sozlesmeBaslik.id) === String(seciliSozlesme));
  const formExtraData = useMemo(() => (
    sozlesmeBaslik?.ekleyen !== null && sozlesmeBaslik?.ekleyen !== undefined
      ? { _override_function: 'Update', ekleyen: sozlesmeBaslik.ekleyen }
      : { _override_function: 'Update' }
  ), [sozlesmeBaslik]);

  const handleFormSuccess = (result) => {
    navigate(-1);
  };

  const handleFormError = (error) => {
    console.error('Form hatası:', error);
    setError('Form işlenirken bir hata oluştu');
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const fetchLokasyonlar = async () => {
    const params = seciliSozlesme
      ? { sozlesme: seciliSozlesme, tip: 'ys' }
      : (formData?.lokasyonId ? { lokasyon: formData.lokasyonId, tip: 'ys' } : null);
    if (!params) return;

    try {
      setLokasyonLoading(true);
      const response = await axios.get(`${API_BASE_URL}/form/lokasyon-listesi`, { params });
      setLokasyonlar(Array.isArray(response.data?.satirlar) ? response.data.satirlar : []);
      setSozlesmeBaslik(response.data?.sozlesme || null);
    } catch (err) {
      console.error('Lokasyonlar yüklenirken hata:', err);
      message.error(err?.response?.data?.detail || 'Lokasyonlar yüklenirken hata oluştu');
    } finally {
      setLokasyonLoading(false);
      if (params.sozlesme) setBaslikDenenen(String(params.sozlesme));
    }
  };

  const fetchAllLokasyonlar = async () => {
    try {
      setLokasyonLoading(true);
      const response = await axios.post(`${API_BASE_URL}/list/execute-query/sozlesme_lokasyonlar`, {
        params: {}
      });
      setLokasyonListesi(response.data || []);
    } catch (err) {
      console.error('Lokasyon listesi yüklenirken hata:', err);
      message.error('Lokasyon listesi yüklenirken hata oluştu');
    } finally {
      setLokasyonLoading(false);
    }
  };

  const handleLokasyonEkle = async () => {
    if (!seciliSozlesme) {
      message.warning('Önce lokasyonun bağlanacağı YS sözleşmesini seçin');
      return;
    }
    if (!selectedLokasyon || !kiraBedeli || !kiraBaslangic) {
      message.warning('Lütfen tüm alanları doldurun');
      return;
    }

    try {
      setLokasyonEklemeLoading(true);
      // Uç {sozlesme, lokasyon} okur; odeme_ay seçilmezse uçta kira başlangıç ayı yazılır
      const res = await axios.post(`${API_BASE_URL}/form/lokasyon-ekle`, {
        sozlesme: seciliSozlesme,
        lokasyon: selectedLokasyon.maptriksID,
        kiraBedeli: parseFloat(kiraBedeli),
        kiraBaslangic: kiraBaslangic.format('YYYY-MM-DD'),
        ...(odemeAy ? { odeme_ay: odemeAy } : {}),
        // kabin boşsa uç aynı sözleşme+lokasyonda ikinci aktif satırı (409) reddeder;
        // çok kabinli kayıt için kabin no girilir
        ...(kabinNo ? { kabin: kabinNo } : {})
      });

      const ay = AYLAR[(res.data?.odeme_ay || 0) - 1];
      message.success(`Lokasyon eklendi (kayıt #${res.data?.inserted_id}${ay ? `, ödeme ayı ${ay}` : ''})`);
      setLokasyonModalVisible(false);
      setSelectedLokasyon(null);
      setKiraBedeli('');
      setKiraBaslangic(null);
      setOdemeAy(null);
      setKabinNo(null);
      fetchLokasyonlar();
    } catch (err) {
      console.error('Lokasyon eklenirken hata:', err);
      message.error(err?.response?.data?.detail || 'Lokasyon eklenirken hata oluştu');
    } finally {
      setLokasyonEklemeLoading(false);
    }
  };

  // Lokasyon sil: yalnız o ys_sozlesme_kayit satırı (id) pasife alınır
  const handleLokasyonSil = async (yskId) => {
    try {
      await axios.delete(`${API_BASE_URL}/form/lokasyon-sil/${yskId}`);
      message.success('Lokasyon kaydı sözleşmeden çıkarıldı');
      fetchLokasyonlar();
    } catch (err) {
      console.error('Lokasyon silinirken hata:', err);
      message.error(err?.response?.data?.detail || 'Lokasyon silinirken hata oluştu');
    }
  };

  const handleLokasyonModalAc = () => {
    setSearchText(''); 
    setLokasyonModalVisible(true);
    fetchAllLokasyonlar();
  };

  const lokasyonKolonlari = [
    {
      title: 'Kayıt',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Sözleşme',
      dataIndex: 'sozlesme',
      key: 'sozlesme',
      width: 220,
      ellipsis: true,
      sorter: (a, b) => (parseInt(a.sozlesme, 10) || 0) - (parseInt(b.sozlesme, 10) || 0),
      render: (v, r) => (
        <span>
          #{v} {r.mUnvan || ''}
          {r.sozlesmeAktif === 0 && <Tag color="red" style={{ marginLeft: 4 }}>Pasif</Tag>}
          {/* 27.10: eski Fedora'da Feshedildi görünen sözleşme burada etiketsizdi (yalnız active'e bakılıyordu) */}
          {r.sozlesmeAktif !== 0 && sozlesmeDurumEtiketi(r)}
        </span>
      ),
    },
    {
      title: 'Lokasyon No',
      dataIndex: 'maptriksID',
      key: 'maptriksID',
      width: 120,
      sorter: (a, b) => (a.maptriksID || 0) - (b.maptriksID || 0),
    },
    {
      title: 'Lokasyon Adı',
      dataIndex: 'Name',
      key: 'Name',
      ellipsis: true,
      sorter: (a, b) => String(a.Name || '').localeCompare(String(b.Name || ''), 'tr'),
    },
    {
      title: 'Kabin',
      dataIndex: 'kabin',
      key: 'kabin',
      width: 70,
      render: (v) => v || '-',
    },
    {
      title: 'Kira Bedeli',
      dataIndex: 'kiraBedeli',
      key: 'kiraBedeli',
      width: 120,
      align: 'right',
      sorter: (a, b) => (a.kiraBedeli || 0) - (b.kiraBedeli || 0),
      render: (value) => formatMoney(value),
    },
    {
      title: 'Kira Başlangıç',
      dataIndex: 'kiraBaslangic',
      key: 'kiraBaslangic',
      width: 130,
      sorter: (a, b) => String(a.kiraBaslangic || '').localeCompare(String(b.kiraBaslangic || '')),
      render: (v) => (v && !String(v).startsWith('0000') ? dayjs(v).format('DD.MM.YYYY') : '-'),
    },
    {
      title: 'Ödeme Ayı',
      dataIndex: 'odeme_ay',
      key: 'odeme_ay',
      width: 100,
      render: (v) => (v ? AYLAR[v - 1] || v : <Text type="warning">0 (boş)</Text>),
    },
    {
      title: 'Sil',
      key: 'sil',
      width: 100,
      render: (_, record) => (
        <Popconfirm
          title={`#${record.id} kaydını (lokasyon ${record.maptriksID}) sözleşmeden çıkarmak istediğinizden emin misiniz?`}
          onConfirm={() => handleLokasyonSil(record.id)}
          okText="Evet"
          cancelText="Hayır"
        >
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
      ),
    },
  ];

  // Modal için lokasyon listesi kolonları
  const modalLokasyonKolonlari = [
    {
      title: 'Lokasyon No',
      dataIndex: 'maptriksID',
      key: 'maptriksID',
      width: 120,
    },
    {
      // 27.10 düzeltmesi: sozlesme_lokasyonlar adı 'Lokasyon Adı' anahtarıyla döndürüyor
      title: 'Lokasyon Adı',
      dataIndex: 'Lokasyon Adı',
      key: 'lokasyonAdi',
      ellipsis: true,
    },
    {
      title: 'Durum',
      dataIndex: 'Durum',
      key: 'durum',
      width: 100,
    },
    {
      title: 'Kategori',
      dataIndex: 'Kategori',
      key: 'kategori',
      width: 120,
    },
    {
      title: 'İl',
      dataIndex: 'İl',
      key: 'il',
      width: 100,
    },
    {
      title: 'İlçe',
      dataIndex: 'İlçe',
      key: 'ilce',
      width: 120,
    },
    {
      title: 'Seç',
      key: 'sec',
      width: 80,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            setSelectedLokasyon(record);
            console.log('Seçilen lokasyon:', record);
            setLokasyonModalVisible(false);
          }}
        >
          Seç
        </Button>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{
        padding: '24px',
        background: darkMode ? 'var(--dark-bg)' : '#f5f5f5',
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        padding: '24px',
        background: darkMode ? 'var(--dark-bg)' : '#f5f5f5',
        minHeight: '100vh'
      }}>
        <Alert
          message="Hata"
          description={error}
          type="error"
          showIcon
          action={
            <Button size="small" onClick={handleGoBack}>
              Geri Dön
            </Button>
          }
        />
      </div>
    );
  }

  if (!formData) {
    return (
      <div style={{
        padding: '24px',
        background: darkMode ? 'var(--dark-bg)' : '#f5f5f5',
        minHeight: '100vh'
      }}>
        <Alert
          message="Uyarı"
          description="Form verisi bulunamadı"
          type="warning"
          showIcon
          action={
            <Button size="small" onClick={handleGoBack}>
              Geri Dön
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{
      padding: '24px',
      background: darkMode ? 'var(--dark-bg)' : '#f5f5f5',
      minHeight: '100vh',
      color: darkMode ? 'var(--dark-text)' : 'inherit'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Breadcrumb */}
        <Breadcrumb
          style={{ 
            marginBottom: 16, 
            color: darkMode ? 'var(--dark-text)' : undefined 
          }}
          items={[
            {
              title: (
                <span onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                  <HomeOutlined /> Ana Sayfa
                </span>
              )
            },
            {
              title: (
                <span onClick={() => navigate('/map')} style={{ cursor: 'pointer' }}>
                  Lokasyonlar
                </span>
              )
            },
            { title: 'Sözleşme Formu' }
          ]}
        />

        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16
        }}>
          <div>
            <Title 
              level={3} 
              style={{ 
                margin: 0,
                color: darkMode ? 'var(--dark-text)' : undefined
              }}
            >
              {formData.title}
            </Title>
          </div>
        </div>

        {/* Form Container */}
        <Card
          style={{
            borderRadius: 12,
            overflow: 'hidden',
            boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.1)',
            background: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
            color: darkMode ? 'var(--dark-text)' : 'inherit',
            border: darkMode ? '1px solid var(--dark-border)' : undefined
          }}
        >
          <div style={{
            maxHeight: 'calc(100vh - 200px)',
            overflowY: 'auto',
            padding: '16px'
          }}>
             {!formData.sozlesmeId && (
               <Space wrap style={{ marginBottom: 12 }}>
                 <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                   YS Sözleşmesi:
                 </Text>
                 <Select
                   style={{ minWidth: 320 }}
                   placeholder={lokasyondakiSozlesmeler.length ? 'Sözleşme seçin' : 'Bu lokasyonda YS sözleşmesi bulunamadı'}
                   value={seciliSozlesme || undefined}
                   onChange={setSeciliSozlesme}
                   options={lokasyondakiSozlesmeler}
                   allowClear
                   onClear={() => setSeciliSozlesme(null)}
                 />
                 {formData.lokasyonId && <Text type="secondary">Lokasyon {formData.lokasyonId}</Text>}
               </Space>
             )}
             {seciliSozlesme && baslikHazir ? (
               <FormikForm
                 key={seciliSozlesme}
                 formId={226}
                 title={`YS Sözleşmesi #${seciliSozlesme}`}
                 tableName="ys_sozlesme"
                 uniqueField="id"
                 uniqueValue={seciliSozlesme}
                 extraData={formExtraData}
                 onSuccess={handleFormSuccess}
                 onError={handleFormError}
               />
             ) : seciliSozlesme ? (
               (lokasyonLoading || baslikDenenen !== String(seciliSozlesme))
                 ? <div style={{ textAlign: 'center', padding: 24 }}><Spin /></div>
                 : (
                   <Alert
                     type="warning"
                     showIcon
                     message={`#${seciliSozlesme} numaralı YS sözleşmesi bulunamadı ya da yüklenemedi; form açılmadı`}
                   />
                 )
             ) : (
               <Alert
                 type="info"
                 showIcon
                 message="Sözleşme formunu açmak için bir YS sözleşmesi seçin"
                 description="Aşağıdaki liste lokasyona bağlı tüm YS kayıtlarını gösterir; yeni lokasyon eklemek için de sözleşme seçilmelidir."
               />
             )}

           </div>
         </Card>

         {/* Sözleşmeye Bağlı Lokasyonlar */}
         <Card
           style={{
             marginTop: 24,
             borderRadius: 12,
             overflow: 'hidden',
             boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.1)',
             background: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
             color: darkMode ? 'var(--dark-text)' : 'inherit',
             border: darkMode ? '1px solid var(--dark-border)' : undefined
           }}
         >
           <div style={{ 
             padding: '16px',
             borderBottom: darkMode ? '1px solid var(--dark-border)' : '1px solid #f0f0f0'
           }}>
             <div style={{
               display: 'flex',
               justifyContent: 'space-between',
               alignItems: 'center'
             }}>
               <Title 
                 level={4} 
                 style={{ 
                   margin: 0,
                   color: darkMode ? 'var(--dark-text)' : undefined
                 }}
               >
                 Sözleşmeye Bağlı Lokasyonlar
               </Title>
               <Button
                 type="primary"
                 icon={<PlusOutlined />}
                 onClick={handleLokasyonModalAc}
                 disabled={!seciliSozlesme}
                 title={seciliSozlesme ? undefined : 'Önce YS sözleşmesi seçin'}
                 className={darkMode ? 'dark-button' : 'light-button'}
               >
                 Lokasyon Ekle
               </Button>
             </div>
           </div>

           <div style={{ padding: '16px' }}>
             <Table
               columns={lokasyonKolonlari}
               dataSource={lokasyonlar}
               loading={lokasyonLoading}
               rowKey="id"
               pagination={{
                 pageSize: 10,
                 showSizeChanger: true,
                 showQuickJumper: true,
                 showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} lokasyon`
               }}
               className={darkMode ? 'dark-table' : 'light-table'}
               scroll={{ x: 800 }}
             />
           </div>
         </Card>

         {/* Lokasyon Ekleme Modal */}
         <Modal
           title="Lokasyon Seçiniz"
           open={lokasyonModalVisible}
           onCancel={() => setLokasyonModalVisible(false)}
           width={1000}
           footer={null}
           className={darkMode ? 'dark-modal' : 'light-modal'}
         >
           <div style={{ marginBottom: 16 }}>
             <Input
               placeholder="Lokasyon ara... (ID, Ad, Durum, Kategori, İl, İlçe)"
               value={searchText}
               onChange={(e) => setSearchText(e.target.value)}
               allowClear
               style={{ width: '100%' }}
               prefix={<SearchOutlined />}
             />
           </div>
           <Table
             columns={modalLokasyonKolonlari}
             dataSource={filteredLokasyonListesi}
             loading={lokasyonLoading}
             rowKey="maptriksID"
             pagination={{
               pageSize: 10,
               showSizeChanger: true,
               showQuickJumper: true,
               pageSizeOptions: ['10', '20', '50', '100'],
               showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} lokasyon`
             }}
             className={darkMode ? 'dark-table' : 'light-table'}
             scroll={{ x: 800 }}
           />
         </Modal>

         {/* Seçilen Lokasyon Ekleme Formu */}
         {selectedLokasyon && (
           <Card
             style={{
               marginTop: 16,
               borderRadius: 12,
               overflow: 'hidden',
               boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.1)',
               background: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
               color: darkMode ? 'var(--dark-text)' : 'inherit',
               border: darkMode ? '1px solid var(--dark-border)' : undefined
             }}
           >
             <div style={{ 
               padding: '16px',
               borderBottom: darkMode ? '1px solid var(--dark-border)' : '1px solid #f0f0f0'
             }}>
               <Title 
                 level={5} 
                 style={{ 
                   margin: 0,
                   color: darkMode ? 'var(--dark-text)' : undefined
                 }}
               >
                 Seçilen Lokasyon: {selectedLokasyon.maptriksID} - {selectedLokasyon['Lokasyon Adı'] || selectedLokasyon.Name || ''}
               </Title>
             </div>

             <div style={{ padding: '16px' }}>
               <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', alignItems: 'end' }}>
                 <div>
                   <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                     Kira Bedeli
                   </Text>
                   <Input
                     type="number"
                     placeholder="Kira bedeli girin"
                     value={kiraBedeli}
                     onChange={(e) => setKiraBedeli(e.target.value)}
                     style={{
                       marginTop: 4,
                       background: darkMode ? 'var(--dark-bg)' : '#fff',
                       borderColor: darkMode ? 'var(--dark-border)' : '#d9d9d9',
                       color: darkMode ? 'var(--dark-text)' : 'inherit'
                     }}
                   />
                 </div>

                 <div>
                   <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                     Kira Başlangıç Tarihi
                   </Text>
                   <DatePicker
                     placeholder="Tarih seçin"
                     value={kiraBaslangic}
                     onChange={setKiraBaslangic}
                     style={{
                       width: '100%',
                       marginTop: 4,
                       background: darkMode ? 'var(--dark-bg)' : '#fff',
                       borderColor: darkMode ? 'var(--dark-border)' : '#d9d9d9'
                     }}
                   />
                 </div>

                 <div>
                   <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                     Kabin No
                   </Text>
                   {/* İsteğe bağlı; aynı lokasyona ikinci kabin satırı için zorunlu */}
                   <InputNumber
                     min={1}
                     max={99}
                     precision={0}
                     placeholder="Boş: tek kayıt"
                     value={kabinNo}
                     onChange={(v) => setKabinNo(v || null)}
                     style={{ width: '100%', marginTop: 4 }}
                   />
                 </div>

                 <div>
                   <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                     Ödeme Ayı
                   </Text>
                   {/* Boş bırakılırsa kira başlangıç ayı yazılır (PHP yolu 0 bırakıyordu) */}
                   <Select
                     style={{ width: '100%', marginTop: 4 }}
                     allowClear
                     placeholder={kiraBaslangic ? `${AYLAR[kiraBaslangic.month()]} (kira başlangıcı)` : 'Kira başlangıç ayı'}
                     value={odemeAy || undefined}
                     onChange={(v) => setOdemeAy(v || null)}
                     options={AYLAR.map((ad, i) => ({ value: i + 1, label: ad }))}
                   />
                 </div>

                 <div>
                   <Space>
                     <Button
                       type="primary"
                       onClick={handleLokasyonEkle}
                       loading={lokasyonEklemeLoading}
                       className={darkMode ? 'dark-button' : 'light-button'}
                     >
                       Ekle
                     </Button>
                     <Button
                       onClick={() => {
                         setSelectedLokasyon(null);
                         setKiraBedeli('');
                         setKiraBaslangic(null);
                         setOdemeAy(null);
                         setKabinNo(null);
                       }}
                     >
                       İptal
                     </Button>
                   </Space>
                 </div>
               </div>
             </div>
           </Card>
         )}
       </div>

      {/* CSS Stilleri */}
      <style jsx>{`
        .dark-button {
          background-color: var(--dark-bg-header) !important;
          border-color: var(--dark-border) !important;
          color: var(--dark-text) !important;
        }
        .dark-button:hover {
          background-color: var(--dark-bg) !important;
          border-color: var(--color-primary) !important;
        }
        .light-button {
          background-color: #fff !important;
          border-color: #d9d9d9 !important;
          color: rgba(0, 0, 0, 0.85) !important;
        }
        .light-button:hover {
          background-color: #f5f5f5 !important;
          border-color: var(--color-primary) !important;
        }
      `}</style>
    </div>
  );
};

const IMZALI_DURUM = [1, 2, 6];
const sozlesmeDurumEtiketi = (r) => {
  const imza = Number(r.sozlesmeImzaDurumu);
  if (!IMZALI_DURUM.includes(imza)) {
    const ad = [3, 4, 5].includes(imza) ? 'Feshedildi' : imza === 99 ? 'Aday (İmzasız)' : imza === 7 ? 'Belediye Kiracısı' : 'İptal';
    return <Tag color="red" style={{ marginLeft: 4 }}>{ad}</Tag>;
  }
  if (r.sozlesmeBitis && dayjs(r.sozlesmeBitis).isBefore(dayjs(), 'day')) {
    return <Tag color="orange" style={{ marginLeft: 4 }}>Süresi Doldu</Tag>;
  }
  return null;
};

export default LokasyonSozlesme;
